import React, { useState, useEffect, useReducer, useContext, useCallback, useRef } from "react";
import { toast } from "react-toastify";
import { useParams, useHistory } from "react-router-dom";
import { listContactListItems, deleteContactListItem, uploadContactListFile } from "../../services/contactLists.api";
import { i18n } from "../../translate/i18n";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";
import { SocketContext } from "../../context/Socket/SocketContext";
import useContactLists from "../../hooks/useContactLists";
import useColumnAliases from "../../hooks/useColumnAliases";

const reducer = (state, action) => {
  if (action.type === "LOAD_CONTACTS") {
    const contacts = action.payload;
    
    // ✅ CORRECCIÓN: Validar que contacts sea un array antes de usar forEach
    if (!Array.isArray(contacts)) {
      console.warn("LOAD_CONTACTS: payload no es un array, usando array vacío");
      return state;
    }
    
    const newContacts = [];

    contacts.forEach((contact) => {
      const contactIndex = state.findIndex((c) => c.id === contact.id);
      if (contactIndex !== -1) {
        state[contactIndex] = contact;
      } else {
        newContacts.push(contact);
      }
    });

    return [...state, ...newContacts];
  }

  if (action.type === "UPDATE_CONTACTS") {
    const contact = action.payload;
    const contactIndex = state.findIndex((c) => c.id === contact.id);

    if (contactIndex !== -1) {
      state[contactIndex] = contact;
      return [...state];
    } else {
      return [contact, ...state];
    }
  }

  if (action.type === "DELETE_CONTACT") {
    const contactId = action.payload;

    const contactIndex = state.findIndex((c) => c.id === contactId);
    if (contactIndex !== -1) {
      state.splice(contactIndex, 1);
    }
    return [...state];
  }

  if (action.type === "RESET") {
    return [];
  }
};

// D8.23.3 — Lógica (12 estados + useReducer + effects + handlers + API) de ContactListItems.
export default function useContactListItems() {

  const { user } = useContext(AuthContext);
  const { contactListId } = useParams();
  const history = useHistory();
  const { getAlias } = useColumnAliases();

  const [loading, setLoading] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [searchParam, setSearchParam] = useState("");
  const [contacts, dispatch] = useReducer(reducer, []);
  const [selectedContactId, setSelectedContactId] = useState(null);
  const [contactListItemModalOpen, setContactListItemModalOpen] =
    useState(false);
  const [deletingContact, setDeletingContact] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [errorModalOpen, setErrorModalOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [hasMore, setHasMore] = useState(false);
  const [contactList, setContactList] = useState({});
  const fileUploadRef = useRef(null);
  
  // ✅ PASO 1: Estados para modal de progreso de importación
  const [importProgressModalOpen, setImportProgressModalOpen] = useState(false);
  const [importProgress, setImportProgress] = useState({
    processed: 0,
    total: 0,
    percentage: 0,
    message: ""
  });

  const { findById: findContactList } = useContactLists();

  const socketManager = useContext(SocketContext);

  useEffect(() => {
    findContactList(contactListId).then((data) => {
      setContactList(data);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contactListId]);

  useEffect(() => {
    dispatch({ type: "RESET" });
    setPageNumber(1);
  }, [searchParam]);

  useEffect(() => {
    setLoading(true);
    const delayDebounceFn = setTimeout(() => {
      const fetchContacts = async () => {
        try {
          const { data } = await listContactListItems({ searchParam, pageNumber, contactListId });
          dispatch({ type: "LOAD_CONTACTS", payload: data.contacts });
          setHasMore(data.hasMore);
          setLoading(false);
        } catch (err) {
          toastError(err);
        }
      };
      fetchContacts();
    }, 500);
    return () => clearTimeout(delayDebounceFn);
  }, [searchParam, pageNumber, contactListId]);

  useEffect(() => {
    const companyId = localStorage.getItem("companyId");
    const socket = socketManager.getSocket(companyId);
    
    // ✅ CORRECCIÓN: Flag para verificar si el componente está montado
    let isMounted = true;

    socket.on(`company-${companyId}-ContactListItem`, (data) => {
      if (data.action === "update" || data.action === "create") {
        dispatch({ type: "UPDATE_CONTACTS", payload: data.record });
      }

      if (data.action === "delete") {
        dispatch({ type: "DELETE_CONTACT", payload: +data.id });
      }

      if (data.action === "reload") {
        // ✅ CORRECCIÓN: Recargar desde la API en lugar de usar data.records (que puede ser undefined)
        // Esto asegura que se obtengan todos los contactos actualizados
        listContactListItems({ searchParam: "", pageNumber: 1, contactListId }).then(({ data: apiData }) => {
          // ✅ CORRECCIÓN: Solo actualizar estado si el componente está montado
          if (isMounted) {
            dispatch({ type: "LOAD_CONTACTS", payload: apiData.contacts || [] });
            setHasMore(apiData.hasMore);
            setPageNumber(1);
          }
        }).catch((err) => {
          if (isMounted) {
            toastError(err);
          }
        });
      }
    });

    socket.on(
      `company-${companyId}-ContactListItem-${contactListId}`,
      (data) => {
        if (data.action === "reload") {
          // ✅ CORRECCIÓN: Recargar desde la API en lugar de usar data.records
          // Esto asegura que se obtengan todos los contactos actualizados
          listContactListItems({ searchParam: "", pageNumber: 1, contactListId }).then(({ data: apiData }) => {
            // ✅ CORRECCIÓN: Solo actualizar estado si el componente está montado
            if (isMounted) {
              dispatch({ type: "LOAD_CONTACTS", payload: apiData.contacts || [] });
              setHasMore(apiData.hasMore);
              setPageNumber(1);
            }
          }).catch((err) => {
            if (isMounted) {
              toastError(err);
            }
          });
        }
      }
    );

    // ✅ PASO 3: Listener para eventos de importación de contactos
    socket.on(`company-${companyId}-importContacts`, (data) => {
      // ✅ CORRECCIÓN: Solo procesar eventos si el componente está montado
      if (!isMounted) return;
      
      // ✅ PASO 3: Manejar evento "start"
      if (data.action === "start") {
        setImportProgress({
          processed: 0,
          total: 0,
          percentage: 0,
          message: data.message || "Iniciando importación..."
        });
        setImportProgressModalOpen(true);
      }
      
      // ✅ PASO 3: Manejar evento "progress"
      if (data.action === "progress") {
        const progress = data.progress || {};
        setImportProgress({
          processed: progress.processed || 0,
          total: progress.total || 0,
          percentage: progress.percentage || 0,
          message: data.message || `Procesando: ${progress.processed || 0}/${progress.total || 0} contactos`
        });
      }
      
      // ✅ PASO 3: Manejar evento "completed"
      if (data.action === "completed") {
        const summary = data.summary || {};
        // ✅ CORRECCIÓN: Mensaje amigable para el usuario (no mostrar código técnico)
        const successCount = summary.success || 0;
        const totalCount = summary.total || 0;
        let friendlyMessage = `Importación completada: ${successCount} de ${totalCount} contactos procesados exitosamente`;
        
        // Agregar información adicional si hay advertencias
        const warnings = [];
        if (summary.duplicate > 0) warnings.push(`${summary.duplicate} duplicados`);
        if (summary.error > 0) warnings.push(`${summary.error} errores`);
        if (summary.invalid_number > 0) warnings.push(`${summary.invalid_number} números inválidos`);
        if (summary.empty_number > 0) warnings.push(`${summary.empty_number} números vacíos`);
        if (summary.short_number > 0) warnings.push(`${summary.short_number} números muy cortos`);
        
        if (warnings.length > 0) {
          friendlyMessage += `. Advertencias: ${warnings.join(', ')}. Revisa el reporte descargado para más detalles.`;
        }
        
        setImportProgress({
          processed: totalCount,
          total: totalCount,
          percentage: 100,
          message: friendlyMessage
        });
        
        // ✅ PASO 3: Descargar reporte si está disponible
        if (data.report) {
          // ✅ CORRECCIÓN: Forzar descarga usando fetch + blob para evitar que el navegador muestre el XML
          try {
            // Normalizar backslashes a forward slashes (por si acaso viene de Windows)
            let reportUrl = String(data.report).replace(/\\/g, '/');
            
            // Asegurar que empiece con '/' para construir la URL correctamente
          if (!reportUrl.startsWith('/')) {
            reportUrl = '/' + reportUrl;
          }
          
          // Construir URL completa
          const backendUrl = window.WATOOLX_BACKEND_URL || process.env.REACT_APP_BACKEND_URL || window.location.origin.replace(':3000', ':8080');
          const downloadUrl = `${backendUrl}${reportUrl}`;
          
            // Descargar usando fetch + blob para forzar descarga (evita que el navegador muestre el XML)
            fetch(downloadUrl)
              .then(response => response.blob())
              .then(blob => {
                const url = window.URL.createObjectURL(blob);
          const link = document.createElement('a');
                link.href = url;
          link.setAttribute('download', 'reporte-importacion.xlsx');
          document.body.appendChild(link);
          link.click();
          link.remove();
                window.URL.revokeObjectURL(url);
              })
              .catch(error => {
                console.error('Error descargando reporte:', error);
                toast.error('Error al descargar el reporte. Por favor, intente nuevamente.');
              });
          } catch (error) {
            console.error('Error procesando descarga:', error);
            toast.error('Error al procesar la descarga del reporte.');
          }
        }
        
        // Mostrar resumen en toast (usar las mismas warnings ya construidas arriba)
        const successMsg = `✅ Importación completada: ${summary.success || 0} procesados`;
        const toastWarnings = [];
        if (summary.duplicate > 0) toastWarnings.push(`${summary.duplicate} duplicados`);
        if (summary.error > 0) toastWarnings.push(`${summary.error} errores`);
        if (summary.invalid_number > 0) toastWarnings.push(`${summary.invalid_number} números inválidos`);
        if (summary.empty_number > 0) toastWarnings.push(`${summary.empty_number} números vacíos`);
        if (summary.short_number > 0) toastWarnings.push(`${summary.short_number} números muy cortos`);
        
        if (toastWarnings.length > 0) {
          toast.warning(`${successMsg}. ⚠️ ${toastWarnings.join(', ')}. Revisa el reporte descargado.`);
        } else {
          toast.success(`${successMsg}. Reporte descargado.`);
        }
        
        // ✅ CORRECCIÓN: Recargar lista de contactos desde la API
        dispatch({ type: "RESET" });
        setPageNumber(1);
        // Recargar contactos
        listContactListItems({ searchParam: "", pageNumber: 1, contactListId }).then(({ data }) => {
          // ✅ CORRECCIÓN: Solo actualizar estado si el componente está montado
          if (isMounted) {
            dispatch({ type: "LOAD_CONTACTS", payload: data.contacts || [] });
            setHasMore(data.hasMore);
          }
        }).catch((err) => {
          if (isMounted) {
            toastError(err);
          }
        });
      }
      
      // ✅ PASO 3: Manejar evento "error"
      if (data.action === "error") {
        if (isMounted) {
          setImportProgressModalOpen(false);
          toastError(new Error(data.message || "Error durante la importación"));
        }
      }
    });

    return () => {
      // ✅ CORRECCIÓN: Marcar componente como desmontado y limpiar todos los listeners
      isMounted = false;
      socket.off(`company-${companyId}-ContactListItem`);
      socket.off(`company-${companyId}-ContactListItem-${contactListId}`);
      socket.off(`company-${companyId}-importContacts`);
      // No desconectar el socket completamente, solo remover los listeners específicos
      // socket.disconnect(); // ❌ NO desconectar - otros componentes pueden estar usando el socket
    };
  }, [contactListId, socketManager]);

  const handleSearch = (event) => {
    setSearchParam(event.target.value.toLowerCase());
  };

  const handleOpenContactListItemModal = () => {
    setSelectedContactId(null);
    setContactListItemModalOpen(true);
  };

  const handleCloseContactListItemModal = () => {
    setSelectedContactId(null);
    setContactListItemModalOpen(false);
  };

  const hadleEditContact = (contactId) => {
    setSelectedContactId(contactId);
    setContactListItemModalOpen(true);
  };

  const handleDeleteContact = async (contactId) => {
    try {
      await deleteContactListItem(contactId);
      toast.success(i18n.t("contacts.toasts.deleted"));
      setDeletingContact(null);
      setSearchParam("");
      setPageNumber(1);
    } catch (err) {
      // ✅ CORRECCIÓN: Mostrar error en modal en lugar de toast
      const errorMsg = err?.response?.data?.error || err?.response?.data?.message || err?.message || "Error al eliminar el contacto";
      setErrorMessage(errorMsg);
      setErrorModalOpen(true);
      // No cerrar el modal de confirmación si hay error, para que el usuario pueda intentar de nuevo
    }
  };

  // ✅ PASO 1: Modificar handleImportContacts para proceso asíncrono
  const handleImportContacts = async () => {
    try {
      const formData = new FormData();
      formData.append("file", fileUploadRef.current.files[0]);
      
      // ✅ PASO 1: Enviar petición y recibir respuesta inmediata (200 OK)
      const response = await uploadContactListFile(contactListId, formData);
      
      // ✅ PASO 1: Si la respuesta es exitosa, abrir modal de progreso
      if (response.status === 200) {
        // Inicializar progreso
        setImportProgress({
          processed: 0,
          total: 0,
          percentage: 0,
          message: "Iniciando importación..."
        });
        // Abrir modal de progreso
        setImportProgressModalOpen(true);
        // Cerrar modal de confirmación
        setConfirmOpen(false);
        }
      
      // ✅ PASO 1: NO esperar respuesta completa - el proceso continúa en background
      // Los eventos Socket.IO actualizarán el progreso en tiempo real
      
    } catch (err) {
      // ✅ PASO 1: Si hay error al iniciar, cerrar modal y mostrar error
      setImportProgressModalOpen(false);
      toastError(err);
    }
  };

  const loadMore = () => {
    setPageNumber((prevState) => prevState + 1);
  };

  const handleScroll = (e) => {
    if (!hasMore || loading) return;
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollHeight - (scrollTop + 100) < clientHeight) {
      loadMore();
    }
  };

  const goToContactLists = () => {
    history.push("/contact-lists");
  };

  const goToCampaigns = () => {
    history.push("/campaigns");
    setErrorModalOpen(false);
  };

  return {
    confirmOpen,
    contactList,
    contactListId,
    contactListItemModalOpen,
    contacts,
    deletingContact,
    dispatch,
    errorMessage,
    errorModalOpen,
    fileUploadRef,
    getAlias,
    goToCampaigns,
    goToContactLists,
    hadleEditContact,
    handleCloseContactListItemModal,
    handleDeleteContact,
    handleImportContacts,
    handleOpenContactListItemModal,
    handleScroll,
    handleSearch,
    hasMore,
    history,
    importProgress,
    importProgressModalOpen,
    loadMore,
    loading,
    pageNumber,
    searchParam,
    selectedContactId,
    setConfirmOpen,
    setContactList,
    setContactListItemModalOpen,
    setDeletingContact,
    setErrorMessage,
    setErrorModalOpen,
    setHasMore,
    setImportProgress,
    setImportProgressModalOpen,
    setLoading,
    setPageNumber,
    setSearchParam,
    setSelectedContactId,
    socketManager,
    user,
  };
}
